import {
    Component,
    Element,
    h,
    Host,
    Listen,
    Prop,
    State,
} from "@stencil/core";
import { track } from "../../utils/tracking";

const scrollAreaMargin: number = 45;
@Component({
    tag: "ux-iws-nb-filter",
    styleUrl: "nb-filter.scss",
})
export class NbFilter {
    @Element() element: HTMLUxIwsNbFilterElement;

    /**
     * The label for the close button in the dropdowns.
     * This will be used to set the `data-close-label` attribute on all `ux-dropdown` elements within this component.
     */
    @Prop() closeLabel: string = "Close";
    /** The label for the scroll left button */
    @Prop() scrollLeftLabel: string = "Scroll left";
    /** The label for the scroll right button */
    @Prop() scrollRightLabel: string = "Scroll right";

    @State() scrollPosition: number = 0;
    @State() windowWidth: number = window.innerWidth;
    @State() scrollAreaWidth: number = 0;
    private scrollInner: HTMLDivElement;

    connectedCallback() {
        this.element.querySelectorAll("ux-dropdown").forEach((dropdown) => {
            dropdown.setAttribute("data-close-label", this.closeLabel);
        });
        window.addEventListener("resize", () => {
            this.updateWindowWidth();
            this.updateScrollAreaWidth();
        });
        this.element.addEventListener("click", (event) => {
            const target = event.target as HTMLElement;
            if (target.classList.contains("ux-dropdown__label")) {
                setTimeout(() => {
                    this.updateWindowWidth();
                    this.updateScrollAreaWidth();
                    this.updateScrollButtons();
                }, 50);
            }
        });
        this.updateWindowWidth();
        this.updateScrollAreaWidth();
    }

    componentDidLoad() {
        this.insertCloseIcons();
    }

    private insertCloseIcons() {
        this.element.querySelectorAll("ux-dropdown").forEach((dropdown) => {
            const closeIcon = document.createElement("ux-icon");
            closeIcon.setAttribute("name", "cross");
            closeIcon.className = "ux-iws-nb-filter-dropdown__close-icon";
            dropdown.insertAdjacentElement("afterend", closeIcon);
        });
    }

    disconnectedCallback() {
        window.removeEventListener("resize", () => {
            this.updateScrollAreaWidth();
        });
    }

    @Listen("uxFilterChange")
    updateScrollAreaWidth() {
        let width = scrollAreaMargin; // Initial width for the scroll buttons
        const scrollItems = this.element.querySelectorAll("ux-form-group");
        scrollItems.forEach((item) => {
            const itemWidth = item.getBoundingClientRect().width;
            width += itemWidth;
        });
        this.scrollAreaWidth = Math.floor(width);
        this.element.style.setProperty(
            "--ux-iws-nb-filter-form-width",
            `${width}px`
        );
    }

    private updateWindowWidth() {
        this.windowWidth = window.innerWidth;
    }

    @Listen("uxFilterChange")
    trackFilter() {
        // currently this only works with ux-checkboxes (e.g. ux-checkbox-list-item)
        const filterData: { [key: string]: string } = {};
        Array.from(this.scrollInner.querySelectorAll("ux-checkbox"))
            .filter((checkbox) => checkbox.checked)
            .forEach((checkbox) => {
                const { name, value } = checkbox;
                if (name && value) {
                    const oldValue = filterData[name];
                    filterData[name] = oldValue
                        ? `${oldValue},${value}`
                        : value;
                }
            });
        track("change_filter", { filters: filterData });
    }

    @Listen("uxFilterChange")
    updateScrollButtons() {
        this.scrollPosition = this.scrollInner.scrollLeft;
    }

    private scroll(direction: "left" | "right") {
        const distance = this.scrollInner.clientWidth - scrollAreaMargin;
        this.scrollInner.scrollBy({
            left: direction === "left" ? -distance : distance,
            behavior: "smooth",
        });
        this.updateScrollButtons();
    }

    render() {
        return (
            <Host>
                <div class="ux-iws-nb-filter__scroll-outer">
                    <button
                        aria-label={this.scrollLeftLabel}
                        class={{
                            "ux-iws-nb-filter__scroll-button ux-iws-nb-filter__scroll-button--left": true,
                            "is-overlapping": this.scrollPosition > 0,
                            "is-deactivated": this.scrollPosition === 0,
                            "is-hidden":
                                this.windowWidth >= this.scrollAreaWidth,
                        }}
                        onClick={() => this.scroll("left")}
                    >
                        <ux-icon name="chevron-left"></ux-icon>
                    </button>
                    <div
                        class="ux-iws-nb-filter__scroll-inner"
                        ref={(el) => (this.scrollInner = el as HTMLDivElement)}
                        onScroll={() => {
                            this.updateScrollButtons();
                        }}
                    >
                        <slot />
                    </div>
                    <button
                        aria-label={this.scrollRightLabel}
                        class={{
                            "ux-iws-nb-filter__scroll-button ux-iws-nb-filter__scroll-button--right": true,
                            "is-overlapping": this.scrollInner
                                ? this.scrollPosition + this.scrollAreaWidth <
                                  this.windowWidth
                                : true,
                            "is-deactivated":
                                this.scrollPosition + this.windowWidth >=
                                this.scrollAreaWidth + scrollAreaMargin,
                            "is-hidden":
                                this.windowWidth >= this.scrollAreaWidth,
                        }}
                        onClick={() => this.scroll("right")}
                    >
                        <ux-icon name="chevron-right"></ux-icon>
                    </button>
                </div>
            </Host>
        );
    }
}
